/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import { t } from 'i18next';

// Component imports
import { Space } from 'antd';
import { MarkerType } from 'reactflow';

// Misc imports
import { SWITCH_QUERY_CONDITIONS } from '../../constants/flowGraph';
import { deserializeLIQE, serializeLIQE } from '../../utils/transform/liqe';
import { FILE_UPLOAD_NODE_HANDLE_IDS, NODE_TYPES, NODE_TYPES_PROPERTIES, SWITCH_QUERY_HANDLE_IDS, TEXT_INPUT_TYPES } from '../../constants/flowGraph';
import { generateRandomString } from '../string/generateRandomString';
import { CHAPTERSOURCE_SCHEMAS, HTTP_REQUEST_CONTENT_TYPES } from '../../constants/main';
import is from '../is';
import clone from '../object/clone';
import startsWith from '../string/startsWith';
import removePrefix from '../string/removePrefix';
import createDetailedError from '../misc/createDetailedError';
import moment from 'moment-timezone';

//#region Helpers

/**
 * Get the valid source node types for a given target node type
 * @param {String} currentNodeType The type of the node we want to get the valid source node types for
 * @param {Array} disabledNodeTypes An array of node types that should be excluded from the list of valid source node types
 */
export const getValidSourceNodeTypes = (currentNodeType, disabledNodeTypes = []) => {
  let validSourceNodeTypes = [];
  Object.entries(NODE_TYPES_PROPERTIES)
    .filter(([_key, nodeProps]) => !disabledNodeTypes.includes(nodeProps.type))
    .forEach(([key, nodeProps]) => {
      if (nodeProps.validTargetNodeTypes.includes(currentNodeType)) {
        validSourceNodeTypes.push(key);
      }
    });
  return validSourceNodeTypes;
};

/**
 * Get the valid target node types for a given source node type
 * @param {String} currentNodeType The type of the node we want to get the valid target node types for
 * @param {Array} disabledNodeTypes An array of node types that should be excluded from the list of valid source node types
 */
export const getValidTargetNodeTypes = (currentNodeId, elements, currentNodeType, disabledNodeTypes) => {
  // Check if the node already has an outgoing edge
  const outgoingEdges = elements.filter(element => element.data.nodeType === 'edge' && element.source === currentNodeId);

  // If the node is of a type that can't have more than one outgoing edge (like OPTION or INTRO), and it already has an edge, return an empty array
  if (outgoingEdges.length > 0 && (currentNodeType === NODE_TYPES.OPTION || currentNodeType === NODE_TYPES.INTRO)) {
    return [];
  }

  // Else
  // Return the valid target node types if the node can still have more outgoing edges
  return NODE_TYPES_PROPERTIES[currentNodeType].validTargetNodeTypes.filter(targetNodeType => !disabledNodeTypes.includes(targetNodeType));
};

/**
 * Determines if a node of a given type can be a standalone based on its properties.
 * @param {String} currentNodeType - The type of the node we want to get the valid target node types for
 * @returns {Boolean} - true / false
 */
export const canBeStandalone = (currentNodeType) => {
  return NODE_TYPES_PROPERTIES[currentNodeType].standalone;
};

/**
 * Get the recalculated priority of an option node according to sibling nodes (if any)
 * @param elements The elements that are currently in the graph
 * @param edgeSourceNode The source node
 * @returns {number} The node's priority number
 */
export const calculateNodePriority = (elements, edgeSourceNode) => {
  // To find the priority, we need to find all other (Option) nodes that are connected to the same parent
  const siblingNodeIds = elements.filter((element) => element.data.nodeType === 'edge' && element.source === edgeSourceNode.id).map(element => element.target);
  const siblingNodes = elements.filter((element) => element.data.nodeType === 'node' && (element.type === NODE_TYPES.OPTION || element.type === NODE_TYPES.LINK_OPTION) && siblingNodeIds.includes(element.id));

  let maxPriority = Math.max.apply(Math, siblingNodes.map(function(element) {
    return element.data?.priority;
  }));

  return (maxPriority && isFinite(maxPriority)) ? maxPriority + 1 : 1;
};

/**
 * Helper function used when something changes in the graph.
 * Goes through everything in the graph and ensures it behaves properly
 * (eg. Message nodes with >1 edge are not connectable anymore etc.)
 * @param {*} elements The current graph elements
 * @returns The sanitized elements
 */
export const sanitizeFlowGraph = (elements) => {
  if (!elements || !is.array(elements)) {
    return [];
  }

  const nodes = elements.filter((element) => element.data.nodeType === 'node');
  const edges = elements.filter((element) => element.data.nodeType === 'edge');

  // Sanitize edges
  // Clean up edges that are not connected anywhere
  let frontendEdges = [];
  edges.forEach(edge => {
    if (nodes.some(node => node.id === edge.source) && nodes.some(node => node.id === edge.target)) {
      frontendEdges.push({
        ...edge,
        markerEnd: {
          type: MarkerType.ArrowClosed
        }
      });
    }
  });

  // Related to the NLP flow editor
  // If an edge's source is the one with id SWITCH_QUERY_HANDLE_IDS.TRUE, then the label of the edge should also say "true" and vice versa
  // If this is not required in the future, we can remove this code
  frontendEdges = frontendEdges.map(edge => {
    const parentNode = nodes.find(node => node.id === edge.source);

    if (parentNode?.type !== NODE_TYPES.QUERY) {
      return edge;
    } else if (edge.sourceHandle === SWITCH_QUERY_HANDLE_IDS.TRUE) {
      return {
        ...edge,
        label: 'true'
      };
    } else if (edge.sourceHandle === SWITCH_QUERY_HANDLE_IDS.FALSE) {
      return {
        ...edge,
        label: 'false'
      };
    } else {
      return edge;
    }
  });

  // Merge nodes and edges
  let frontendGraph = [...nodes, ...frontendEdges];
  return frontendGraph;
};

/**
 * Validates and sanitizes current edges in the graph, called when a new edge is connected in the graph
 * @param {Array} elements The elements that are currently in the graph
 * @param {Array} elementsToUpdate The elements we plan to update
 * @param {Object} newEdge The edge being added in the graph
 * @returns New elements after edge sanitization
 */
export const validateFlowGraphEdges = (elements, elementsToUpdate, newEdge) => {
  // Check if the edge source-target can be connected
  const nodes = elements.filter((element) => element.data.nodeType === 'node');
  const edgeSourceNode = nodes.find(node => node.id === newEdge.source);
  const edgeTargetNode = nodes.find(node => node.id === newEdge.target);
  let siblingNodeIds = [];
  let siblingNodes = [];

  const isValidConnection = NODE_TYPES_PROPERTIES[edgeSourceNode.type].validTargetNodeTypes.includes(edgeTargetNode.type);

  // Check if the connection is valid
  if (!isValidConnection) {
    return Promise.reject(
      <Space size="small">
        Valid target nodes are the following:
        {
          // TODO: Change it to display node types as little nodes (pretty!)
          NODE_TYPES_PROPERTIES[edgeSourceNode.type].validTargetNodeTypes.map(nodeType => NODE_TYPES_PROPERTIES[nodeType].label).join(', ')
        }
      </Space>
    );
  }

  // Source node checks:
  switch (edgeSourceNode.type) {
    // A "Select" node should be able to connect only to "Option" or "Text Input" nodes.
    // No mix between the two should be allowed
    case NODE_TYPES.SELECT:
      siblingNodeIds = elements.filter(element => element.data.nodeType === 'edge' && element.source === edgeSourceNode.id).map(element => element.target);
      siblingNodes = elements.filter(element => element.data.nodeType === 'node' && siblingNodeIds.includes(element.id));

      // Sibling nodes should also contain the current target node
      siblingNodes.push(edgeTargetNode);

      // Ensure there is no more than one Text Input node per each Select node
      if (siblingNodes.filter(node => node.type === NODE_TYPES.TEXT_INPUT).length > 1) {
        return Promise.reject('Only one Text Input node is allowed for each Question node.');
      }

      // Reset the "render options as"/isDropdown/preferredRendering setting for irrelevant nodes
      if (![NODE_TYPES.MULTI_OPTION, NODE_TYPES.DYNAMIC_MULTI_OPTION].includes(edgeTargetNode.type)) {
        elementsToUpdate = elementsToUpdate.map(element => {
          if (element.id === edgeSourceNode.id) {
            return {
              ...element,
              data: {
                ...element.data,
                isDropdown: false
              }
            };
          } else {
            return element;
          }
        });
      }
      if ([NODE_TYPES.LINK_OPTION].includes(edgeTargetNode.type)) {
        elementsToUpdate = elementsToUpdate.map(element => {
          if (element.id === edgeSourceNode.id) {
            return {
              ...element,
              data: {
                ...element.data,
                preferredRendering: 'buttons'
              }
            };
          } else {
            return element;
          }
        });
      } else {
        elementsToUpdate = elementsToUpdate.map(element => {
          if (element.id === edgeSourceNode.id) {
            return {
              ...element,
              data: {
                ...element.data,
                preferredRendering: undefined
              }
            };
          } else {
            return element;
          }
        });
      }
      break;

    default:
      break;
  }

  // Target node checks:
  switch (edgeTargetNode.type) {
    // When an "Option" node becomes connected, its priority needs to be recalculated
    case NODE_TYPES.OPTION:
    case NODE_TYPES.LINK_OPTION:
      var nodePriority = calculateNodePriority(elements, edgeSourceNode);
      elementsToUpdate = elementsToUpdate.map(element => {
        if (element.id === edgeTargetNode.id) {
          return {
            ...element,
            data: {
              ...element.data,
              priority: nodePriority
            }
          };
        } else {
          return element;
        }
      });
      break;

    // When a dynamic multi-option node becomes connected, we need to ensure there are no other similar nodes on the same level
    case NODE_TYPES.DYNAMIC_MULTI_OPTION:
      // Find all other nodes that are connected to the same parent
      siblingNodeIds = elements.filter((element) => element.data.nodeType === 'edge' && element.source === edgeSourceNode.id).map(element => element.target);
      siblingNodes = elements.filter((element) => element.data.nodeType === 'node' && siblingNodeIds.includes(element.id));

      // Do not allow multiple dynamic nodes with the same parent
      if (siblingNodes.filter(node => node.type === NODE_TYPES.DYNAMIC_MULTI_OPTION).length > 0) {
        return Promise.reject('You cannot have more than one dynamic multi-option node on the same level.');
      }
      break;

    default:
      break;
  }

  // Misc checks:
  // Do not allow mixing any option types together (Option, Multi-Option, Dynamic Multi-Option, User Input)
  if ([(NODE_TYPES.OPTION || NODE_TYPES.LINK_OPTION), NODE_TYPES.MULTI_OPTION, NODE_TYPES.DYNAMIC_MULTI_OPTION, NODE_TYPES.TEXT_INPUT].includes(edgeTargetNode.type)) {
    siblingNodeIds = elementsToUpdate.filter((element) => element.data.nodeType === 'edge' && element.source === edgeSourceNode.id).map(element => element.target);
    siblingNodes = elementsToUpdate.filter((element) => element.data.nodeType === 'node' && siblingNodeIds.includes(element.id));

    if (siblingNodes.some(node =>
      node.type !== edgeTargetNode.type
      && ([NODE_TYPES.OPTION, NODE_TYPES.MULTI_OPTION, NODE_TYPES.DYNAMIC_MULTI_OPTION, NODE_TYPES.TEXT_INPUT].includes(node.type))
    )) {
      return Promise.reject('You cannot mix different option types.');
    }
  }

  elementsToUpdate = sanitizeFlowGraph(elementsToUpdate);

  return Promise.resolve(elementsToUpdate);
};

/**
 * Validates the flowgraph, called upon submitting the flow
 * @param {Array} elements The elements that are currently in the graph
 * @param {String} primaryBotLanguage The primary bot language
 * @param {String} currentBotLanguage The current bot language the bot is being edited in
 * @returns A promise that resolves if the flowgraph is valid, and rejects with an error message if it's not
 */
export const validateFlowGraphBeforeSubmit = (elements, primaryBotLanguage, currentBotLanguage) => {
  // Check if there is at least one Intro node
  if (!elements.some(element => element.data.nodeType === 'node' && element.type === NODE_TYPES.INTRO)) {
    return Promise.reject(new Error(t('You need to have at least one Start node in your flow.')));
  }

  // Check if there are any standalone nodes
  const nodes = elements.filter((element) => element.data.nodeType === 'node');
  const edges = elements.filter((element) => element.data.nodeType === 'edge');
  const standaloneNodes = nodes.filter(node => !canBeStandalone(node.type) && !edges.some(edge => edge.source === node.id || edge.target === node.id));

  if (standaloneNodes.length > 0) {
    return Promise.reject(new Error(t('There are detached nodes in your flow. Please connect them to the flow.')));
  }

  // Flow control nodes
  // Check if flow controls are not fully connected
  const switchQueryNodes = nodes.filter(node => node.type === NODE_TYPES.SWITCH_QUERY);

  for (const switchQueryNode of switchQueryNodes) {
    const targets = edges.filter(arrow => arrow.source === switchQueryNode.id);

    // If we don't have two target nodes for flow control node, then we need to return a UI error
    if (targets.length < 2) {
      return Promise.reject(createDetailedError(t(`Flow Control Nodes should have two target nodes, one for truthy and one for falsy results`), { nodeId: switchQueryNode.id } ));
    }
  }

  // Carousel nodes
  const carouselNodes = nodes.filter(node => node.type === NODE_TYPES.CAROUSEL);

  // Do not allow carousel nodes with disconnected buttons
  for (const carouselNode of carouselNodes) {
    const buttons = carouselNode.data.cards.flatMap(card => {
      return card[primaryBotLanguage]?.buttons || [];
    });

    for (const button of buttons) {
      const cardOfButton = carouselNode.data.cards.find(card => card[primaryBotLanguage].buttons.includes(button));
      const buttonIndex = cardOfButton[primaryBotLanguage].buttons.indexOf(button);
      if (
        !button?.data // If the button has no URL set
        && !edges.some(edge => edge.sourceHandle === `${cardOfButton[primaryBotLanguage].id}${buttonIndex !== 0 ? `-${buttonIndex}` : ''}`) // If the button is not connected to a node
      ) {
        return Promise.reject(createDetailedError(t(`Please connect all buttons in Carousel nodes`), { nodeId: carouselNode.id }));
      }
    }
  }

  // Do not allow partially filled carousel nodes in secondary languages
  for (const carouselNode of carouselNodes) {
    // The card key is not the primary language
    for (const card of carouselNode.data.cards) {
      const cardData = card[currentBotLanguage];

      // The number of buttons in the primary language
      const primaryLanguageButtons = card[primaryBotLanguage]?.buttons || [];
      // The number of buttons in the current language
      const currentLanguageButtons = cardData?.buttons || [];

      // If the number of buttons in the current language is less than the number of buttons in the primary language
      // Or if any of the buttons in the current language are missing a title
      if (currentLanguageButtons.length < primaryLanguageButtons.length || currentLanguageButtons.some(button => !button?.title)) {
        return Promise.reject(createDetailedError(t(`Please add translations to all buttons in Carousel nodes`), { nodeId: carouselNode.id }));
      }
    }
  }

  // File upload nodes
  // Check if there is a file upload node, and validate its outgoing edges
  const fileUploadNodes = nodes.filter(node => node.type === NODE_TYPES.FILE_UPLOAD);

  for (const fileUploadNode of fileUploadNodes) {
    const requiredHandles = [
      ...(fileUploadNode.data.supportedFileTypes?.length > 0 ? [FILE_UPLOAD_NODE_HANDLE_IDS.UNSUPPORTED_TYPE] : []),
      ...(fileUploadNode.data.maxFileSize ? [FILE_UPLOAD_NODE_HANDLE_IDS.EXCEEDS_SIZE] : [])
    ];

    const missingCases = requiredHandles.filter(handleId => {
      const fullHandleId = `${fileUploadNode.id}-${handleId}`;
      return !edges.some(edge => edge.sourceHandle === fullHandleId);
    });

    if (missingCases.length > 0) {
      const caseDescriptions = missingCases.map(handleId => {
        switch (handleId) {
          case FILE_UPLOAD_NODE_HANDLE_IDS.UNSUPPORTED_TYPE:
            return t('Not accepted file format');
          case FILE_UPLOAD_NODE_HANDLE_IDS.EXCEEDS_SIZE:
            return t('Not accepted file size');
          default:
            return t('Unknown case');
        }
      }).join(', ');

      return Promise.reject(createDetailedError(t(`Please connect cases in File Upload nodes: ${caseDescriptions}.`), { nodeId: fileUploadNode.id }));
    }
  }

  return Promise.resolve();
};

//#endregion

//#region Chaptersources

/**
 * Transform a chaptersource into a reactflow graph
 * @param {Object} elements The data contents for a chaptersource
 * @param {String} primaryBotLanguage The primary bot language, needed for the initial node label
 * @returns {Array} The list of nodes and edges
*/
export const chaptersourceToFlowGraph = (elements, primaryBotLanguage) => {
  if (!elements) {
    return [];
  }

  if (!elements.content) {
    return [];
  }

  const { nodes = [], edges = [] } = elements.content;

  // Transform nodes
  let frontendNodes = [...nodes].map(node => {
    let formattedNode = {
      id: node.id,
      type: node.type,
      width: node.width,
      height: node.height,
      position: node.position ? {
        x: Math.round(node.position.x / 10) * 10, // Snap to grid
        y: Math.round(node.position.y / 10) * 10  // Snap to grid
      } : { x: 0, y: 0 },
      data: {
        nodeType: 'node',
        label: node.label || { [primaryBotLanguage]: '' }
      }
    };

    switch (node.type) {
      case NODE_TYPES.INTRO:
        formattedNode.data = {
          ...formattedNode.data,
          // Intro nodes have a setting for whether we want the session to reset when we get there
          resetSession: node.resetSession
        };
        break;

      case NODE_TYPES.MESSAGE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.inlineFeedbackData && { inlineFeedbackData: node.inlineFeedbackData },
          ...node.enableBackAction && { enableBackAction: node.enableBackAction }
        };
        break;

      case NODE_TYPES.SELECT:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.enableBackAction && { enableBackAction: node.enableBackAction },
          ...node.questionId && { questionId: node.questionId },
          ...node.friendlyName && { friendlyName: node.friendlyName },
          isDropdown: node.isDropdown || false,
          ...node.preferredRendering && { preferredRendering: node.preferredRendering },
          ...node.submitLabel && { submitLabel: node.submitLabel },
          ...node.sendboxState && { sendboxState: node.sendboxState }
        };
        break;

      case NODE_TYPES.OPTION:
      case NODE_TYPES.LINK_OPTION:
        formattedNode.data = {
          ...formattedNode.data,
          tooltip: node.tooltip,
          // Option nodes also have a priority setting that marks the order they appear in the agents
          priority: node.priority || 1,
          // Also specific case for link-options
          ...node.type === NODE_TYPES.LINK_OPTION && { linkUrl: node.linkUrl }
        };
        break;

      case NODE_TYPES.TEXT_INPUT:
        formattedNode.data = {
          ...formattedNode.data,
          inputType: node.inputType || TEXT_INPUT_TYPES.STRING,
          validationFallbackMessage: node.validationFallbackMessage,
          inputMaxCharacters: node.inputMaxCharacters,
          inputIsMandatory: node.inputIsMandatory != null ? node.inputIsMandatory : true,
          inputIsMultiLine: node.inputIsMultiLine,
          submitLabel: node.submitLabel
        };
        break;

      case NODE_TYPES.INPUT_QUERY:
        formattedNode.data = {
          ...formattedNode.data,
          inputType: node.inputType || TEXT_INPUT_TYPES.STRING,
          inputIsMandatory: node.inputIsMandatory || false,
          renderAsCard: node.renderAsCard || false,
          ...node.validationFallbackMessage && { validationFallbackMessage: node.validationFallbackMessage },
          ...node.timesToValidate && { timesToValidate: node.timesToValidate },
          ...node.placeholder && { placeholder: node.placeholder },
          ...node.customRegex && { customRegex: node.customRegex },
          ...node.inputMaxCharacters && { inputMaxCharacters: node.inputMaxCharacters },
          ...node.inputIsMultiLine && { inputIsMultiLine: node.inputIsMultiLine },
          ...node.exitKeyWords && { exitKeyWords: node.exitKeyWords },
          ...node.questionId && { questionId: node.questionId },
          ...node.submitLabel && { submitLabel: node.submitLabel },
          ...node.skipLabel && { skipLabel: node.skipLabel },
          ...node.minDate && { minDate: { type: node.minDate.type, value: moment(node.minDate.value) } },
          ...node.maxDate && { maxDate: { type: node.maxDate.type, value: moment(node.maxDate.value) } }
        };
        break;

      case NODE_TYPES.FILE_UPLOAD:
        formattedNode.data = {
          ...formattedNode.data,
          questionId: node.questionId,
          fileUploadFallbackMessage: node.fileUploadFallbackMessage,
          ...node.sendboxState && { sendboxState: node.sendboxState },
          ...node.supportedFileTypes && { supportedFileTypes: node.supportedFileTypes },
          ...node.maxFileSize && { maxFileSize: node.maxFileSize },
          ...node.maxFileSizeUnit && { maxFileSizeUnit: node.maxFileSizeUnit }
        };
        break;

      case NODE_TYPES.MULTI_OPTION:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.multiOptions && { multiOptions: node.multiOptions }
        };
        break;

      case NODE_TYPES.DYNAMIC_MULTI_OPTION:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.dynamicDataKey && { dynamicDataKey: startsWith(node.dynamicDataKey, 'templateData.') ? removePrefix(node.dynamicDataKey, 'templateData.') : node.dynamicDataKey }
        };
        break;

      case NODE_TYPES.SWITCH_VARIABLE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.switchVariable && { switchVariable: node.switchVariable }
        };
        break;

      case NODE_TYPES.SWITCH_QUERY: {
        const switchQuery = { ...node.switchQuery };

        for (const language in node.switchQuery){
          const deserialisedExpression = deserializeLIQE(node.switchQuery[language]);

          const firstListItem = deserialisedExpression?.[0]?.list?.[0];
          if (firstListItem?.condition === SWITCH_QUERY_CONDITIONS.MATCHES_REGEX && firstListItem?.value.endsWith('/')) {
            firstListItem.value = firstListItem.value.slice(0, -1);
            switchQuery[language] = serializeLIQE(deserialisedExpression);
          }
        }

        formattedNode.data = {
          ...formattedNode.data,
          ...node.switchQuery && { switchQuery: switchQuery }
        };

        break;
      }

      case NODE_TYPES.SWITCH_CASE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.switchCaseData && { switchCaseData: node.switchCaseData }
        };
        break;

      case NODE_TYPES.SEND_STORED_DATA:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.sendStoredData && {
            sendStoredData: {
              ...node.sendStoredData,
              headers: !is.emptyObject(node.sendStoredData.headers) ? node.sendStoredData.headers : { '': '' }
            }
          }
        };
        break;

      case NODE_TYPES.HTTP_REQUEST:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.friendlyName && { friendlyName: node.friendlyName },
          ...node.httpRequestData && {
            httpRequestData: {
              ...node.httpRequestData,
              headers: !is.emptyObject(node.httpRequestData.headers) ? node.httpRequestData.headers : { }
            }
          }
        };
        break;

      case NODE_TYPES.EMAIL:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.email && { email: node.email }
        };
        break;

      case NODE_TYPES.JUMP:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.jumpTo && { jumpTo: node.jumpTo }
        };
        break;

      case NODE_TYPES.LIVECHAT:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.livechatChannelId && { livechatChannelId: node.livechatChannelId },
          ...node.azureTenantId && { azureTenantId: node.azureTenantId },
          ...node.livechatGroup && { livechatGroup: node.livechatGroup }
        };
        break;

      case NODE_TYPES.LIVECHAT_REQUEST:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.livechatGroup && { livechatGroup: node.livechatGroup },
          ...node.requestExtras && { requestExtras: JSON.stringify(node.requestExtras) }
        };
        break;

      case NODE_TYPES.MEDIA:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.mediaUrl && { mediaUrl: node.mediaUrl },
          ...node.mediaType && { mediaType: node.mediaType },
          ...node.posterUrl && { posterUrl: node.posterUrl }
        };
        break;

      case NODE_TYPES.ANALYTIC_TAGS:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.analyticTags && { analyticTags: node.analyticTags },
          ...node.analyticTagsRemoved && { analyticTagsRemoved: node.analyticTagsRemoved }
        };
        break;

      case NODE_TYPES.USER_INFO:
        formattedNode.data = {
          ...formattedNode.data,
          includeEmail: node.includeEmail || false,
          includeFullName: node.includeFullName || false,
          includePhone: node.includePhone || false,
          ...node.validationFallbackMessage && { validationFallbackMessage: node.validationFallbackMessage },
          ...node.customUserInfoData && { customUserInfoData: node.customUserInfoData },
          ...node.submitLabel && { submitLabel: node.submitLabel }
        };
        break;

      case NODE_TYPES.USER_FEEDBACK:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.jumpTo && { jumpTo: node.jumpTo },
          jumpProbability: node.jumpProbability || 1
        };
        break;

      case NODE_TYPES.VARIABLE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.variable && { variable: node.variable }
        };
        break;

      case NODE_TYPES.ACTION:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.actionData && { actionData: node.actionData },
          ...node.altText && { altText: node.altText }
        };
        break;

      case NODE_TYPES.LLM:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.friendlyName && { friendlyName: node.friendlyName },
          ...node.llmData && { llm: node.llmData }
        };
        break;

      case NODE_TYPES.SEMANTIC_SEARCH:
        formattedNode.data = {
          ...formattedNode.data,
          ...node,
          ...node.variableName && { variableName: node.variableName }
        };
        break;

      case NODE_TYPES.CAROUSEL:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.cards && {
            cards: clone(node.cards).map(card => {
              const languageKeys = Object.keys(card);
              languageKeys.forEach(language => {
                // Context: If a button is connected to a node then its `data` property should be null
                //          If a button is not connected to a node, it has a URL to redirect to, which is stored in `data` property
                // Backend implementation: The `buttonToEdgeMap` array should ONLY store buttons that redirect to a node
                //                         The `buttons` array should ONLY store buttons that redirect to a URL
                // The following performs the required operations to map data from the mentioned arrays to form data:
                // 1) The `buttons` already has all the buttons that redirect to a URL
                // 2) For each `buttonToEdgeMap` button that redirects to a node, find the value of the `order` property
                // 3) Construct a `button` object and add it to the list of `card.buttons`, we use .splice() to place it in the correct order relative to other buttons
                if (card[language]?.buttonToEdgeMap?.length > 0) {
                  card[language].buttonToEdgeMap?.forEach((buttonEdge) => {
                    if (card[language].buttons) {
                      card[language].buttons.splice(buttonEdge?.order || 0, 0, {
                        title: buttonEdge?.label
                      });
                    } else {
                      card[language].buttons = [{
                        title: buttonEdge?.label
                      }];
                    }
                  });
                }
              });
              return card;
            })
          }
        };
        break;

      case NODE_TYPES.CUSTOM_RESPONSE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.jsonResponse && { jsonResponse: node.jsonResponse }
        };
        break;

      case NODE_TYPES.CSAT:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.ratingStyle && { ratingStyle: node.ratingStyle },
          ...node.sections && { sections: node.sections },
          ...node.submitLabel && { submitLabel: node.submitLabel },
          ...node.variableName && { variableName: node.variableName }
        };
        break;

      default:
        break;
    }

    return formattedNode;
  });

  // Transform edges
  let frontendEdges = [...edges].map((edge) => {
    const formattedEdge = {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'default',
      arrowHeadType: 'arrowclosed',
      label: edge.label || '',
      data: {
        label: edge.label,
        nodeType: 'edge'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed
      },
      sourceHandle: edge.sourcePositionId || edge.source
    };
    return formattedEdge;
  });

  // Merge nodes and edges
  let frontendGraph = [...frontendNodes, ...frontendEdges];
  return sanitizeFlowGraph(frontendGraph);
};

/**
 * Transform a reactflow graph into a chaptersource
 * @param {Array} elements The list of nodes and edges
 * @param {Object} settings Any extra settings for the generated chaptersource
 * @param {Object} layout The layout of the graph
 * @param {String} primaryBotLanguage The primary bot language, needed for the initial node label
 * @returns {Object} An object that contains the data contents for a chaptersource
 */
export const flowGraphToChaptersource = (elements, settings, layout, primaryBotLanguage) => {
  // Select nodes & edges separately
  // Also remove possibly invalid elements added by react-flow
  const nodes = elements.filter((element) => element.data.nodeType === 'node' && is.truthy(element.type));
  const edges = elements.filter((element) => element.data.nodeType === 'edge' && is.truthy(element.source) && is.truthy(element.target));

  // Transform nodes
  let backendNodes = [...nodes].map((node) => {
    let formattedNode = {
      id: node.id,
      type: node.type,
      variable: node.data.variable,
      position: node.position,
      width: node.width,
      height: node.height,
      // node.data
      label: node.data.label,
      enableBackAction: node.data.enableBackAction,
      isDropdown: node.data.isDropdown,
      preferredRendering: node.data.preferredRendering,
      priority: node.data.priority,
      linkUrl: node.data.linkUrl,
      tooltip: node.data.tooltip
    };

    switch (node.type) {
      case NODE_TYPES.INTRO:
        formattedNode.resetSession = node.data.resetSession;
        break;

      case NODE_TYPES.MESSAGE:
        if (node.data.inlineFeedbackData) {
          formattedNode.inlineFeedbackData = node.data.inlineFeedbackData;
        }
        break;

      case NODE_TYPES.SELECT:
        if (node.data.submitLabel) {
          formattedNode.submitLabel = node.data.submitLabel;
        }
        if (node.data.questionId) {
          formattedNode.questionId = node.data.questionId;
        }
        if (node.data.friendlyName) {
          formattedNode.friendlyName = node.data.friendlyName;
        }
        if (node.data.sendboxState) {
          formattedNode.sendboxState = node.data.sendboxState;
        }
        break;

      case NODE_TYPES.TEXT_INPUT:
        formattedNode.inputType = node.data.inputType || TEXT_INPUT_TYPES.STRING;
        formattedNode.validationFallbackMessage = node.data.validationFallbackMessage;
        formattedNode.inputMaxCharacters = node.data.inputMaxCharacters;
        formattedNode.inputIsMandatory = node.data.inputIsMandatory;
        formattedNode.inputIsMultiLine = node.data.inputIsMultiLine;
        break;

      case NODE_TYPES.INPUT_QUERY:
        formattedNode.inputType = node.data.inputType || TEXT_INPUT_TYPES.STRING;
        formattedNode.validationFallbackMessage = (node.data.inputType && node.data.inputType !== TEXT_INPUT_TYPES.STRING) ? node.data.validationFallbackMessage : undefined;
        formattedNode.inputIsMandatory = node.data.inputIsMandatory;
        formattedNode.renderAsCard = typeof node.data.renderAsCard === 'boolean' ? node.data.renderAsCard : true;
        formattedNode.timesToValidate = (node.data.inputType && node.data.inputType !== TEXT_INPUT_TYPES.STRING) ? node.data.timesToValidate : undefined;
        formattedNode.customRegex = (node.data.inputType && node.data.inputType === TEXT_INPUT_TYPES.CUSTOM_REGEX) ? node.data.customRegex : undefined;
        formattedNode.placeholder = node.data.placeholder;
        formattedNode.inputMaxCharacters = node.data.inputMaxCharacters;
        formattedNode.inputIsMultiLine = node.data.inputIsMultiLine;
        formattedNode.exitKeyWords = node.data.exitKeyWords;
        formattedNode.questionId = node.data.questionId;
        formattedNode.submitLabel = node.data.submitLabel;
        formattedNode.skipLabel = node.data.skipLabel;
        formattedNode.minDate = node.data.minDate?.type ? node.data.minDate : undefined;
        formattedNode.maxDate = node.data.maxDate?.type ? node.data.maxDate : undefined;
        break;

      case NODE_TYPES.FILE_UPLOAD:
        formattedNode.questionId = node.data.questionId;
        formattedNode.fileUploadFallbackMessage = node.data.fileUploadFallbackMessage;
        formattedNode.sendboxState = node.data.sendboxState;
        formattedNode.supportedFileTypes = node.data.supportedFileTypes;
        formattedNode.maxFileSize = node.data.maxFileSize;
        formattedNode.maxFileSizeUnit = node.data.maxFileSizeUnit;
        break;

      case NODE_TYPES.MULTI_OPTION:
        formattedNode.multiOptions = node.data.multiOptions;
        break;

      case NODE_TYPES.DYNAMIC_MULTI_OPTION:
        if (node.data.dynamicDataKey) {
          formattedNode.dynamicDataKey = !startsWith(node.data.dynamicDataKey, 'templateData.') ? 'templateData.'.concat(node.data.dynamicDataKey) : node.data.dynamicDataKey;
        }
        break;

      case NODE_TYPES.STICKY_NOTE:
        formattedNode.sticky_note = node.data.sticky_note;
        break;

      case NODE_TYPES.SWITCH_VARIABLE:
        formattedNode.switchVariable = node.data.switchVariable;
        break;
      case NODE_TYPES.SWITCH_QUERY:
        formattedNode.switchQuery = node.data.switchQuery;

        if (formattedNode.switchQuery) {
          Object.keys(formattedNode.switchQuery).forEach(key => {
            const value = formattedNode.switchQuery[key];
            const deserialisedExpression = deserializeLIQE(value);

            if (value === '()') {
              formattedNode.switchQuery[key] = '';
            }

            if (value === 'undefined') {
              formattedNode.switchQuery[key] = '';
            }

            if (Array.isArray(value) && value.length === 0) {
              formattedNode.switchQuery[key] = '';
            }

            const firstListItem = deserialisedExpression?.[0]?.list?.[0];
            const isRegexMatch = firstListItem?.condition === SWITCH_QUERY_CONDITIONS.MATCHES_REGEX;
            const valueIsString = typeof value === 'string';

            if (isRegexMatch && valueIsString && value.at(-2) !== '/') {
              const switchQueryChars = value.split('');
              switchQueryChars.splice(-1, 0, '/');
              formattedNode.switchQuery[key] = switchQueryChars.join('');
            }
          });
        }

        break;
      case NODE_TYPES.SWITCH_CASE:
        formattedNode.switchCaseData = node.data.switchCaseData;
        break;

      case NODE_TYPES.SEND_STORED_DATA:
        formattedNode.sendStoredData = node.data.sendStoredData;
        formattedNode.sendStoredData.callbackId = node.data.sendStoredData.callbackId || generateRandomString(6);

        if (formattedNode.sendStoredData.headers) {
          formattedNode.sendStoredData.headers = Object.fromEntries(Object.entries(formattedNode.sendStoredData.headers).filter(([_, v]) => v !== null && v !== '')); // Remove empty headers
        }
        break;

      case NODE_TYPES.HTTP_REQUEST:
        delete node.data.httpRequestData.urlSearchParams;
        formattedNode.httpRequestData = node.data.httpRequestData;

        if (formattedNode.httpRequestData.headers) {
          formattedNode.httpRequestData.headers = Object.fromEntries(Object.entries(formattedNode.httpRequestData.headers).filter(([_, v]) => v !== null && v !== '')); // Remove empty headers
        }

        if (formattedNode.httpRequestData.contentType === HTTP_REQUEST_CONTENT_TYPES.FORM_URLENCODED.key) {
          formattedNode.httpRequestData.body = null;
        }

        if (formattedNode.httpRequestData.contentType === HTTP_REQUEST_CONTENT_TYPES.JSON.key) {
          formattedNode.httpRequestData.formData = null;
        }

        if (node.data.friendlyName) {
          formattedNode.friendlyName = node.data.friendlyName;
        }

        break;

      case NODE_TYPES.EMAIL:
        formattedNode.email = {
          address: node.data.email?.address,
          subject: node.data.email?.subject || '',
          body: node.data.email?.body || ''
        };
        break;

      case NODE_TYPES.JUMP:
        formattedNode.jumpTo = node.data.jumpTo;
        break;

      case NODE_TYPES.LIVECHAT:
        formattedNode.livechatChannelId = node.data.livechatChannelId;
        formattedNode.azureTenantId = node.data.azureTenantId;
        formattedNode.livechatGroup = node.data.livechatGroup;
        break;

      case NODE_TYPES.LIVECHAT_REQUEST:
        formattedNode.livechatGroup = node.data.livechatGroup;
        formattedNode.requestExtras = node.data.requestExtras ? JSON.parse(node.data.requestExtras) : {};
        break;

      case NODE_TYPES.MEDIA:
        formattedNode.mediaUrl = node.data.mediaUrl;
        formattedNode.mediaType = node.data.mediaType;
        formattedNode.posterUrl = node.data.posterUrl;
        break;

      case NODE_TYPES.ANALYTIC_TAGS:
        formattedNode.analyticTags = node.data.analyticTags;
        formattedNode.analyticTagsRemoved = node.data.analyticTagsRemoved;
        break;

      case NODE_TYPES.USER_INFO:
        formattedNode.submitLabel = node.data.submitLabel;
        formattedNode.includeEmail = node.data.includeEmail;
        formattedNode.includeFullName = node.data.includeFullName;
        formattedNode.includePhone = node.data.includePhone;
        formattedNode.customUserInfoData = node.data.customUserInfoData;
        formattedNode.validationFallbackMessage = node.data.validationFallbackMessage;
        break;

      case NODE_TYPES.USER_FEEDBACK:
        formattedNode.jumpTo = node.data.jumpTo;
        formattedNode.jumpProbability = node.data.jumpProbability || 1;
        break;

      case NODE_TYPES.VARIABLE:
        formattedNode.variable = node.data.variable;
        break;

      case NODE_TYPES.ACTION:
        formattedNode.actionData = node.data.actionData;
        if (node.data.altText) {
          formattedNode.altText = node.data.altText;
        }
        break;

      case NODE_TYPES.CAROUSEL:
        formattedNode.cards = clone(node.data.cards);
        var newCards = [];
        formattedNode.cards.forEach((card, cardIndex) => {
          const languageKeys = Object.keys(card);
          languageKeys.forEach(language => {
            // Context: If a button is connected to a node then its `data` property should be null
            //          If a button is not connected to a node, it has a URL to redirect to, which is stored in `data` property
            // Backend implementation: The `buttonToEdgeMap` array should ONLY store buttons that redirect to a node
            //                         The `buttons` array should ONLY store buttons that redirect to a URL
            // The following performs the required operations to map data from the submitted values to the mentioned arrays:
            // 1) Store the indexes of all buttons with the `button.data` property null
            // 2) Create the `buttonToEdgeMap` array, retrieve the title from the original array and add the property `order` to define which place this button belongs to relative to the other buttons
            // 3) For the buttons that redirect to a URL filter out the buttons with a null `button.data` value
            const edgesFromCard = edges.filter((edge) => edge.sourceHandle?.startsWith(`${card[primaryBotLanguage].id}`) );
            const buttonIndexesWithoutUrls = card[primaryBotLanguage]?.buttons.map((button, index) => {
              if (!button.data) {
                return index;
              } else {
                return null;
              }
            }).filter((index) => index !== null);

            const newLanguageKey = {
              ...card[language],
              buttonToEdgeMap: edgesFromCard ? edgesFromCard.map((edge) => {
                const displayOrderOfButton = buttonIndexesWithoutUrls[0];
                if (displayOrderOfButton >= 0) {
                  const edgeToReturn = {
                    edgeId: edge.id,
                    label: card[language].buttons[displayOrderOfButton]?.title || '',
                    order: displayOrderOfButton
                  };
                  buttonIndexesWithoutUrls.shift();
                  return edgeToReturn;
                }

                return null;
              }).filter((edge) => edge !== null) : [],
              buttons: card[language].buttons
                .map((button, index) => {
                  if(card[primaryBotLanguage].buttons[index]?.data) {
                    return {
                      title: button?.title,
                      type: 'url',
                      ...(button?.data && { data: button.data })
                    };
                  }

                  return null;
                })
                .filter((button => button !== null))
            };

            newCards[cardIndex] = {
              ...newCards[cardIndex],
              [language]: newLanguageKey
            };
          });
        });

        formattedNode.cards = newCards;
        break;

      case NODE_TYPES.LLM:
        if (node.data.llm) {
          formattedNode.llmData = node.data.llm;
        }

        if (node.data.friendlyName) {
          formattedNode.friendlyName = node.data.friendlyName;
        }

        break;

      case NODE_TYPES.SEMANTIC_SEARCH:
        if (node.data.variableName) {
          formattedNode.variableName = node.data.variableName;
        }
        formattedNode = {
          ...node.data,
          ...formattedNode
        };
        break;
      case NODE_TYPES.CUSTOM_RESPONSE:
        formattedNode.jsonResponse = node.data?.jsonResponse;
        break;

      case NODE_TYPES.CSAT:
        formattedNode.ratingStyle = node.data.ratingStyle;
        formattedNode.sections = node.data.sections;
        formattedNode.submitLabel = node.data.submitLabel;
        formattedNode.variableName = node.data.variableName;
        break;

      default:
        break;
    }
    return formattedNode;
  });

  // Transform edges
  let backendEdges = [...edges].map((edge) => {
    const formattedEdge = {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.data.label || '',
      ...(edge.sourceHandle && { sourcePositionId: edge.sourceHandle })
    };

    return formattedEdge;
  });

  // Merge nodes and edges & wrap them in an object
  let backendGraph = {
    content: {
      schema: CHAPTERSOURCE_SCHEMAS.FLOWGRAPH,
      layout: layout,
      nodes: [...backendNodes],
      edges: [...backendEdges]
    },
    settings: {
      ...settings
    }
  };
  return backendGraph;
};

//#endregion

//#region NLP Flows

/**
 * Transform a NLP flow into a reactflow graph
 * @param {Object} elements The data contents for a NLP flow
 * @param {String} primaryBotLanguage The primary bot language
 * @returns {Array} The list of nodes and edges
*/
export const nlpFlowToFlowGraph = (elements, primaryBotLanguage) => {
  if (!elements) {
    return [];
  }

  const { nodes = [], edges = [] } = elements;

  // Transform nodes
  let frontendNodes = [...nodes].map(node => {
    let formattedNode = {
      id: node.id,
      type: node.type,
      width: node.width,
      height: node.height,
      position: node.position ? {
        x: Math.round(node.position.x / 10) * 10, // Snap to grid
        y: Math.round(node.position.y / 10) * 10  // Snap to grid
      } : { x: 0, y: 0 },
      data: {
        nodeType: 'node',
        label: node.label || { [primaryBotLanguage]: '' }
      }
    };

    switch (node.type) {
      case NODE_TYPES.QUERY:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.query && { query: node.query }
        };
        break;

      case NODE_TYPES.PIPELINE:
        formattedNode.data = {
          ...formattedNode.data,
          ...node.pipelineId && { pipelineId: node.pipelineId }
        };
        break;

      default:
        break;
    }

    return formattedNode;
  });

  // Transform edges
  let frontendEdges = [...edges].map((edge) => {
    const formattedEdge = {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      type: 'default',
      arrowHeadType: 'arrowclosed',
      label: edge.label || '',
      data: {
        label: edge.label,
        nodeType: 'edge'
      },
      markerEnd: {
        type: MarkerType.ArrowClosed
      },
      sourceHandle: edge.label
    };
    return formattedEdge;
  });

  // Merge nodes and edges
  let frontendGraph = [...frontendNodes, ...frontendEdges];
  return sanitizeFlowGraph(frontendGraph);
};

/**
 * Transform a reactflow graph into a NLP Flow
 * @param {Array} elements The list of nodes and edges
 * @param {String} name The name of the NLP Flow
 * @param {String} language The language of the NLP Flow
 * @returns {Object} An object that contains the data contents for a NLP Flow
 */
export const flowGraphToNlpFlow = (elements, name, language) => {
  // Select nodes & edges separately
  // Also remove possibly invalid elements added by react-flow
  const nodes = elements.filter((element) => element.data.nodeType === 'node' && is.truthy(element.type));
  const edges = elements.filter((element) => element.data.nodeType === 'edge' && is.truthy(element.source) && is.truthy(element.target));

  // Transform nodes
  let backendNodes = [...nodes].map((node) => {
    let formattedNode = {
      id: node.id,
      type: node.type,
      position: node.position,
      width: node.width,
      height: node.height,
      label: node.data.label
    };

    switch (node.type) {
      case NODE_TYPES.QUERY:
        formattedNode.query = node.data.query;
        break;

      case NODE_TYPES.PIPELINE:
        formattedNode.pipelineId = node.data.pipelineId;
        break;

      default:
        break;
    }
    return formattedNode;
  });

  // Transform edges
  let backendEdges = [...edges].map((edge) => {
    const formattedEdge = {
      id: edge.id,
      source: edge.source,
      target: edge.target,
      label: edge.data.label || edge.label || '',
      ...(edge.sourceHandle && { sourcePositionId: edge.sourceHandle })
    };

    return formattedEdge;
  });

  // Merge nodes and edges & wrap them in an object
  let backendGraph = {
    name: name,
    language: language,
    nodes: [...backendNodes],
    edges: [...backendEdges]
  };
  return backendGraph;
};

//#endregion
